import { Monitor, Moon, Sun } from 'lucide-react';

import { useTheme, type ThemePreference } from '@core/hooks/useTheme';
import { useTranslation } from '@core/i18n';
import { cn } from '@core/utils/cn';

const OPTIONS = [
  ['light', Sun],
  ['system', Monitor],
  ['dark', Moon],
] as const satisfies readonly (readonly [ThemePreference, typeof Sun])[];

const ORDER: ThemePreference[] = ['light', 'dark', 'system'];

/**
 * A three-way control, not a two-way switch.
 *
 * "System" has to stay reachable: a user who set it and then toggled once could
 * otherwise never get back to following the OS, and on a laptop that switches at sunset
 * the difference is noticeable.
 *
 * Collapsed, it becomes a single cycling button. Three stacked buttons in a 64px rail is
 * 84px of vertical chrome for something used once a month.
 */
export function ThemeToggle({ collapsed = false }: { collapsed?: boolean }) {
  const { preference, setPreference } = useTheme();
  const { t } = useTranslation();

  const label: Record<ThemePreference, string> = {
    light: t.common.light,
    system: t.common.system,
    dark: t.common.dark,
  };

  if (collapsed) {
    const CurrentIcon = OPTIONS.find(([value]) => value === preference)?.[1] ?? Monitor;
    const next = ORDER[(ORDER.indexOf(preference) + 1) % ORDER.length]!;

    return (
      <button
        type="button"
        onClick={() => setPreference(next)}
        aria-label={`${t.common.theme}: ${label[preference]}`}
        className={cn(
          'text-fg-muted grid size-8 place-items-center rounded-md',
          'hover:bg-fill-subtle hover:text-fg transition-colors duration-150',
          'focus-visible:outline-accent outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
        )}
      >
        <CurrentIcon className="size-4" />
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label={t.common.theme}
      className="rounded-pill bg-fill-subtle ring-separator flex gap-0.5 p-0.5 ring-1 ring-inset"
    >
      {OPTIONS.map(([value, Icon]) => (
        <button
          key={value}
          type="button"
          onClick={() => setPreference(value)}
          aria-label={label[value]}
          aria-pressed={preference === value}
          className={cn(
            'rounded-pill grid size-7 place-items-center transition-colors duration-150',
            'focus-visible:outline-accent outline-none focus-visible:outline-2 focus-visible:outline-offset-1',
            // bg-OVERLAY, not bg-surface. The rail is already `bg-surface`, so in dark
            // mode a selected chip painted the same colour vanished into it -- the
            // control read as a hole punched in the footer rather than a raised segment.
            preference === value
              ? 'bg-overlay text-fg shadow-e1 ring-separator ring-1 ring-inset'
              : 'text-fg-subtle hover:text-fg',
          )}
        >
          <Icon className="size-3.5" />
        </button>
      ))}
    </div>
  );
}
